import React, { useEffect, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import './AdminMessagesPage.css';
import './AdminMessagesPage_Mobile.css';

export default function AdminMessagesPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toAll, setToAll] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
        // Filter to only verified, non-admin, non-deleted users
        const verifiedCustomers = (res.data.users || []).filter(user => 
          !user.isAdmin && user.isEmailVerified && !user.isDeleted
        );
        setUsers(verifiedCustomers);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return setError('Please add subject and notification message');
    try {
      const payload = { to: toAll ? 'all' : selectedUser, title, body };
      await axios.post(`${process.env.REACT_APP_API}/api/users/admin/message`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Notification sent successfully');
      setError('');
      setTitle(''); setBody(''); setSelectedUser(''); setToAll(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to send notification');
    }
  };

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>

      <div className="admin-messages-container">
        {/* Page Header */}
        <motion.div 
          className="messages-page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="messages-page-title">Admin Notifications</h1>
          <p className="messages-page-subtitle">
            Send notifications and announcements to verified customers only.
          </p>
        </motion.div>

        {/* Success/Error Alerts */}
        <AnimatePresence>
          {success && (
            <motion.div 
              className="messages-alert success"
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {success}
            </motion.div>
          )}
          
          {error && (
            <motion.div 
              className="messages-alert error"
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <div className="messages-loading-container">
            <div className="messages-loading-spinner" />
            <div className="messages-loading-text">Loading customers...</div>
          </div>
        ) : (
          <motion.div 
            className="messages-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {/* Recipient Selection */}
            <div className="messages-recipient-section">
              <h2 className="messages-recipient-title">Select Recipients</h2>
              
              <motion.div 
                className="messages-toggle-container"
                whileTap={{ scale: 0.98 }}
              >
                <label className="messages-toggle-label">
                  <input 
                    type="checkbox" 
                    className="messages-toggle-input"
                    checked={toAll} 
                    onChange={e => setToAll(e.target.checked)}
                    aria-label="Send to all customers"
                  />
                  <span className="messages-toggle-text">
                    📢 Send to all verified customers ({users.length} total)
                  </span>
                </label>
              </motion.div>

              <AnimatePresence>
                {!toAll && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <select 
                      className="messages-recipient-select" 
                      value={selectedUser} 
                      onChange={e => setSelectedUser(e.target.value)}
                      aria-label="Select customer"
                    >
                      <option value="">-- Select a customer --</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} — {u.email} ({u.points} points)
                        </option>
                      ))}
                    </select>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Message Form */}
            <form onSubmit={handleSend} className="messages-form">
              <div className="messages-form-group">
                <label className="messages-form-label" htmlFor="message-title">
                  Subject
                </label>
                <motion.input
                  id="message-title"
                  className="messages-input"
                  placeholder="Enter notification subject..."
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  whileFocus={{ scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                  aria-required="true"
                />
              </div>

              <div className="messages-form-group">
                <label className="messages-form-label" htmlFor="message-body">
                  Message
                </label>
                <motion.textarea
                  id="message-body"
                  className="messages-textarea"
                  placeholder="Type your notification here..."
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={8}
                  whileFocus={{ scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                  aria-required="true"
                />
                {body.length > 0 && (
                  <div className="messages-char-counter">
                    {body.length} characters
                  </div>
                )}
              </div>

              <div className="messages-actions">
                <motion.button 
                  type="submit" 
                  className="messages-btn messages-btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!title.trim() || !body.trim() || (!toAll && !selectedUser)}
                  aria-label="Send notification"
                >
                  Send Notification
                </motion.button>
                <motion.button 
                  type="button" 
                  className="messages-btn messages-btn-secondary"
                  onClick={() => { 
                    setTitle(''); 
                    setBody(''); 
                    setSelectedUser(''); 
                    setToAll(false); 
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label="Reset form"
                >
                  Reset
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
